import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth';
import { useSimpleLabel } from '@/lib/prefs';
import { fmtDate } from '@/lib/format';
import { Clock, Plus, ChevronLeft, ChevronRight, Users, Trash2, Pencil, ShieldCheck } from 'lucide-react';

const CATEGORIES = ['design', 'site_visit', 'admin', 'client_review', 'rework', 'meetings', 'training', 'other'];
const CAT_LABEL = {
  design: 'Design', site_visit: 'Site visit', admin: 'Admin', client_review: 'Client review',
  rework: 'Rework', meetings: 'Meetings', training: 'Training', other: 'Other',
};

const LEAD_ROLES = ['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture', 'hr'];

function mondayOf(dateStr) {
  const d = new Date(dateStr);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function shortDow(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short' });
}

export default function TimesheetsPage() {
  const { user } = useAuth();
  const t = useSimpleLabel('timesheet');
  const isLead = LEAD_ROLES.includes(user.role);
  const [tab, setTab] = useState('me');
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date().toISOString().slice(0, 10)));
  const [meData, setMeData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [addEntry, setAddEntry] = useState(null);
  const [editEntry, setEditEntry] = useState(null);

  const loadMe = async () => {
    try {
      const [m, ps] = await Promise.all([
        api.get(`/timesheets/me?week=${weekStart}`),
        api.get('/projects'),
      ]);
      setMeData(m.data); setProjects(ps.data || []);
    } catch (e) { toast.error(parseErr(e)); }
  };
  const loadTeam = async () => {
    try {
      const t = await api.get(`/timesheets/team?week=${weekStart}`);
      setTeamData(t.data);
    } catch (e) { toast.error(parseErr(e)); }
  };

  useEffect(() => { loadMe(); if (isLead) loadTeam(); }, [weekStart]); // eslint-disable-line

  const projMap = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const days = meData?.days || Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const saveEntry = async () => {
    try {
      const body = { ...addEntry };
      if (body.project_id === '__none__') body.project_id = null;
      body.hours = Number(body.hours);
      await api.post('/timesheets', body);
      toast.success('Entry logged'); setAddEntry(null); loadMe();
    } catch (e) { toast.error(parseErr(e)); }
  };
  const updateEntry = async () => {
    try {
      const patch = {
        hours: Number(editEntry.hours),
        task_category: editEntry.task_category,
        project_id: editEntry.project_id === '__none__' ? null : editEntry.project_id,
        notes: editEntry.notes || null,
      };
      await api.patch(`/timesheets/${editEntry.id}`, patch);
      toast.success('Entry updated'); setEditEntry(null); loadMe();
    } catch (e) { toast.error(parseErr(e)); }
  };
  const removeEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`/timesheets/${id}`); toast.success('Entry deleted'); loadMe(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <AppShell breadcrumbs={[{ label: t('page_title', 'Timesheets') }]}>
      <div className="p-6 max-w-[1400px] mx-auto fade-up" data-testid="timesheets-page">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2"><Clock size={22} />{t('page_title', 'Timesheets')}</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">{t('subtitle', 'Log time daily. Under 60 seconds. Non-project time (admin, training) counts too.')}</p>
          </div>
          <WeekNav weekStart={weekStart} setWeekStart={setWeekStart} />
        </div>

        <div className="rounded-lg border p-3 mb-4 bg-[hsl(var(--surface-1))] flex items-start gap-2" style={{ borderColor: 'hsl(var(--border))' }} data-testid="ts-rhythm-note">
          <ShieldCheck size={16} className="mt-0.5 flex-none text-[hsl(var(--success))]" />
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] leading-snug">
            {t('rhythm_note', 'This is a rhythm keeper, not a monitoring tool. Leadership sees compliance and project totals — not your minutes. A logged, unresolved blocker is a system issue, not your failure.')}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-3">
          <TabsList>
            <TabsTrigger value="me" data-testid="ts-tab-me"><Clock size={14} className="mr-1" />{t('tab_me', 'My week')}</TabsTrigger>
            {isLead && <TabsTrigger value="team" data-testid="ts-tab-team"><Users size={14} className="mr-1" />{t('tab_team', 'Team rollup')}</TabsTrigger>}
          </TabsList>
        </Tabs>

        {tab === 'me' && meData && (
          <div className="space-y-3">
            <div className="card p-3" data-testid="ts-summary">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">This week</div>
                  <div className="font-heading text-2xl tabular-nums">{meData.summary.total_hours}h</div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {Object.entries(meData.summary.by_category || {}).map(([c, h]) => (
                    <span key={c} className="chip" data-testid={`ts-cat-${c}`}>{CAT_LABEL[c] || c}: {h}h</span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-7 gap-1 mt-3">
                {days.map(d => (
                  <div key={d} className="text-center border rounded-md py-2" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`ts-day-${d}`}>
                    <div className="text-[10.5px] text-[hsl(var(--foreground-muted))]">{shortDow(d)}</div>
                    <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">{d.slice(5)}</div>
                    <div className="font-heading text-[15px] mt-1 tabular-nums">{meData.summary.by_day[d] || 0}h</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button size="sm" onClick={() => setAddEntry({ date: days[0], project_id: '__none__', hours: '', task_category: 'design', notes: '' })} data-testid="ts-add-cta"><Plus size={13} className="mr-1" />{t('add_button', 'Log time')}</Button>
            </div>

            <div className="card overflow-x-auto">
              <table className="tbl" data-testid="ts-entries-table">
                <thead><tr><th>Date</th><th>Project</th><th>Category</th><th>Hours</th><th>Notes</th><th></th></tr></thead>
                <tbody>
                  {(meData.entries || []).length === 0 && <tr><td colSpan={6} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No entries yet for this week — log time to get started.</td></tr>}
                  {(meData.entries || []).map(e => (
                    <tr key={e.id} data-testid={`ts-entry-${e.id}`}>
                      <td className="text-[12.5px] whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="text-[12.5px]">{e.project_name || <span className="text-[hsl(var(--foreground-subtle))]">—</span>}</td>
                      <td><span className="chip">{CAT_LABEL[e.task_category] || e.task_category}</span></td>
                      <td className="tabular-nums text-[13px]">{e.hours}h</td>
                      <td className="text-[12.5px] max-w-[280px] truncate" title={e.notes || ''}>{e.notes || ''}</td>
                      <td className="text-right">
                        <button className="p-1" onClick={() => setEditEntry({ ...e, project_id: e.project_id || '__none__' })} data-testid={`ts-edit-${e.id}`}><Pencil size={13} /></button>
                        <button className="p-1 text-[hsl(var(--danger))]" onClick={() => removeEntry(e.id)} data-testid={`ts-del-${e.id}`}><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'team' && teamData && (
          <div className="card overflow-x-auto">
            <table className="tbl" data-testid="ts-team-table">
              <thead>
                <tr>
                  <th>Person</th><th>Role</th><th>Hours</th>
                  {teamData.days.map(d => <th key={d} className="text-center text-[11px]">{shortDow(d)}</th>)}
                </tr>
              </thead>
              <tbody>
                {teamData.rows.map(r => (
                  <tr key={r.user_id} data-testid={`ts-team-row-${r.user_id}`}>
                    <td className="font-medium text-[13px]">{r.name || r.email}</td>
                    <td className="text-[12px] capitalize text-[hsl(var(--foreground-muted))]">{r.role?.replace('_', ' ')}</td>
                    <td className="tabular-nums text-[13px]">{r.total_hours}h</td>
                    {teamData.days.map(d => (
                      <td key={d} className="text-center text-[12px] tabular-nums">
                        {r.by_day[d] ? r.by_day[d] : <span className="text-[hsl(var(--foreground-subtle))]">·</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!addEntry} onOpenChange={o => !o && setAddEntry(null)}>
        <DialogContent data-testid="ts-add-dialog">
          <DialogHeader><DialogTitle>Log time</DialogTitle></DialogHeader>
          {addEntry && (
            <div className="grid grid-cols-2 gap-3">
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Date</span>
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={addEntry.date} onChange={e => setAddEntry({ ...addEntry, date: e.target.value })} data-testid="ts-add-date">
                  {days.map(d => <option key={d} value={d}>{shortDow(d)} · {d}</option>)}
                </select></label>
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Hours</span>
                <Input type="number" step="0.5" min="0.5" max="24" value={addEntry.hours} onChange={e => setAddEntry({ ...addEntry, hours: e.target.value })} data-testid="ts-add-hours" /></label>
              <label className="col-span-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Project</span>
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={addEntry.project_id} onChange={e => setAddEntry({ ...addEntry, project_id: e.target.value })} data-testid="ts-add-project">
                  <option value="__none__">— No project (admin/training) —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></label>
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Category</span>
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={addEntry.task_category} onChange={e => setAddEntry({ ...addEntry, task_category: e.target.value })} data-testid="ts-add-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
                </select></label>
              <label className="col-span-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">{t('notes_hint', "Notes (optional — start with 'blocker' or 'waiting' to surface in Wednesday brief)")}</span>
                <Input value={addEntry.notes} onChange={e => setAddEntry({ ...addEntry, notes: e.target.value })} placeholder="What did you work on?" /></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEntry(null)}>Cancel</Button>
            <Button onClick={saveEntry} disabled={!addEntry?.hours || Number(addEntry?.hours) <= 0} data-testid="ts-add-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editEntry} onOpenChange={o => !o && setEditEntry(null)}>
        <DialogContent data-testid="ts-edit-dialog">
          <DialogHeader><DialogTitle>Edit entry — {editEntry?.date}</DialogTitle></DialogHeader>
          {editEntry && (
            <div className="grid grid-cols-2 gap-3">
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Hours</span>
                <Input type="number" step="0.5" min="0.5" max="24" value={editEntry.hours} onChange={e => setEditEntry({ ...editEntry, hours: e.target.value })} /></label>
              <label><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Category</span>
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={editEntry.task_category} onChange={e => setEditEntry({ ...editEntry, task_category: e.target.value })}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
                </select></label>
              <label className="col-span-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Project</span>
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={editEntry.project_id} onChange={e => setEditEntry({ ...editEntry, project_id: e.target.value })}>
                  <option value="__none__">— No project —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></label>
              <label className="col-span-2"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Notes</span>
                <Input value={editEntry.notes || ''} onChange={e => setEditEntry({ ...editEntry, notes: e.target.value })} /></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
            <Button onClick={updateEntry}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function WeekNav({ weekStart, setWeekStart }) {
  const label = `${weekStart} → ${addDays(weekStart, 6)}`;
  return (
    <div className="flex items-center gap-1" data-testid="ts-week-nav">
      <Button size="sm" variant="outline" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="ts-week-prev"><ChevronLeft size={14} /></Button>
      <span className="px-3 py-1.5 text-[12.5px] border rounded-md tabular-nums" style={{ borderColor: 'hsl(var(--border))' }}>{label}</span>
      <Button size="sm" variant="outline" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="ts-week-next"><ChevronRight size={14} /></Button>
      <Button size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(new Date().toISOString().slice(0, 10)))} data-testid="ts-week-today">This week</Button>
    </div>
  );
}
